import React, { useEffect, useState } from "react";
import { Card, Col, Row, Form, ProgressBar, Spinner } from "react-bootstrap";
import IconButton from "components/common/iconButton";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import AdvanceTable from "components/common/advance-table/advanceTable";
import AdvanceTableProvider from "providers/advanceTableProvider";
import AdvanceTableSearchBox from "components/common/advance-table/advanceTableSearchBox";
import ManualPaginationFooter from "components/common/advance-table/manualPaginationFooter";
import useServerAdvanceTable from "hooks/useServerAdvanceTable";
import paths from "routes/paths";
import corner4 from "assets/img/illustrations/corner-4.png";
import Background from "components/common/background";
import { getInitialPageSize } from "../../assets/js/common";
import { useAppContext } from "providers/appProvider";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarAlt } from "@fortawesome/free-solid-svg-icons";

const getDateRangeForOption = (option) => {
  switch (option) {
    case "currentWeek":
      return {
        startDate: moment().startOf("isoWeek").toDate(),
        endDate: moment().endOf("isoWeek").toDate(),
      };
    case "nextWeek":
      return {
        startDate: moment().add(1, "week").startOf("isoWeek").toDate(),
        endDate: moment().add(1, "week").endOf("isoWeek").toDate(),
      };
    case "currentMonth":
      return {
        startDate: moment().startOf("month").toDate(),
        endDate: moment().endOf("month").toDate(),
      };
    case "all":
    default:
      return {
        startDate: moment().subtract(6, "months").toDate(),
        endDate: moment().add(6, "months").toDate(),
      };
  }
};

const Orders = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [filterOption, setFilterOption] = useState(() => {
    return localStorage.getItem("ordersFilterOption") || "all";
  });
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [dateRangeFrom, setDateRangeFrom] = useState(() => {
    const saved = localStorage.getItem("ordersFilterOption") || "all";
    return getDateRangeForOption(saved).startDate;
  });
  const [dateRangeTo, setDateRangeTo] = useState(() => {
    const saved = localStorage.getItem("ordersFilterOption") || "all";
    return getDateRangeForOption(saved).endDate;
  });
  const [isFromOpen, setIsFromOpen] = useState(false);
  const [isToOpen, setIsToOpen] = useState(false);

  // ── Server-side pagination state ──
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(getInitialPageSize("orderTable"));
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);


  const {
    config: { location: userLocation },
  } = useAppContext();

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput), 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (!userLocation?.LocationId || !dateRangeFrom || !dateRangeTo) return;

    const fetchPage = async () => {
      setPageLoading(true);
      try {
        const customerId = localStorage.getItem("customerId");
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/orderRoute/orderOverviewdataPaginated`,
          {
            params: {
              locationId: userLocation.LocationId,
              startDate: moment(dateRangeFrom).format("YYYY-MM-DD"),
              endDate: moment(dateRangeTo).format("YYYY-MM-DD"),
              customerId,
              page: pageIndex + 1,
              pageSize,
              search: debouncedSearch,
            },
            headers: { Authorization: process.env.REACT_APP_BasicAuthSecret },
          }
        );
        setOrders(res.data.orders || []);
        setTotalPages(res.data.totalPages || 0);
        setTotalCount(res.data.totalCount || 0);
      } catch (err) {
        console.error(err);
      } finally {
        setPageLoading(false);
      }
    };

    fetchPage();
  }, [userLocation?.LocationId, dateRangeFrom, dateRangeTo, pageIndex, pageSize, debouncedSearch]);

  useEffect(() => {
    setPageIndex(0);
  }, [dateRangeFrom, dateRangeTo, debouncedSearch]);

  const handleViewDetails = (order) => {
    const path = paths.orderDetails.replace(":orderId", order.OrderId.toLowerCase());
    navigate(path);
  };

  const getFilterLabel = () => {
    if (filterOption === "currentWeek" || filterOption === "nextWeek") {
      return `${t("weekLabel")} : ${moment(dateRangeFrom).isoWeek()}`;
    }
    return "";
  };

  const columns = [
    { accessorKey: "OrderNumber", header: t("orderNumber") },
    { accessorKey: "ClientName", header: t("client") },
    {
      accessorKey: "TemplateName",
      header: t("workType"),
      cell: ({ row: { original } }) => <span>{original.TemplateName}</span>,
    },
    {
      accessorKey: "Cleaners",
      header: t("assignCleaner"),
      cell: ({ row }) => {
        const names = row.original.Cleaners || [];
        return <span>{names.join(", ") || "--"}</span>;
      },
    },
    {
      accessorKey: "StartDate",
      header: t("plannedStartDate"),
      cell: ({ row }) => (
        <span>
          {row.original.StartDate ? moment(row.original.StartDate).format("DD-MM-YYYY") : "--"}
        </span>
      ),
    },
    {
      accessorKey: "EndDate",
      header: t("plannedEndDate"),
      cell: ({ row }) => (
        <span>
          {row.original.EndDate ? moment(row.original.EndDate).format("DD-MM-YYYY") : "--"}
        </span>
      ),
    },
    {
      accessorKey: "Status",
      header: t("status"),
      cell: ({ row }) => {
        const status = row.original.Status?.toLowerCase();
        const progressMap = { draft: 25, active: 50, paused: 75, completed: 100 };
        const variantMap = { draft: "primary", active: "info", paused: "danger", completed: "success" };
        const progress = progressMap[status] || 0;
        const variant = variantMap[status] || "dark";
        return (
          <ProgressBar now={progress} variant={variant} style={{ height: "8px" }} visuallyHidden />
        );
      },
    },
  ];

  const table = useServerAdvanceTable ({
    data: orders,
    columns,
    selection: false,
    sortable: true,
    pagination: true,
    manualPagination: true,
    pageCount: totalPages,
    paginationState: { pageIndex, pageSize },
    onPaginationChange: (updater) => {
      const next = typeof updater === "function" ? updater({ pageIndex, pageSize }) : updater;
      setPageIndex(next.pageIndex);
      setPageSize(next.pageSize);
    },
  });

  if (pageLoading && orders.length === 0) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <AdvanceTableProvider {...table}>
      <Row className="g-3 mb-3">
        <Col lg={12}>
          <Card className="mb-3 h-100">
            <Background image={corner4} className="bg-card" />
            <Card.Body className="position-relative pe-3 pb-2">
              <Row className="flex-between-center">
                <Col md>
                  <h5 className="mb-2 mb-md-0">{t("orders")}</h5>
                </Col>
                <Col xs="auto">
                  <IconButton as={Link} to={paths.newOrder} variant="primary" size="sm" icon="plus" transform="shrink-3" data-tour="new-order-btn">
                    <span className="ms-1">{t("newOrder")}</span>
                  </IconButton>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="mb-3">
        <Card.Header>
          <Row className="align-items-center justify-content-between">
            <Col xs={12} sm={4} md={4}>
              <p className="fw-semi-bold mb-0 data-font-size data-font-colour">
                {getFilterLabel()}
              </p>
            </Col>

            <Col xs={12} sm={8} md={8} className="d-flex justify-content-end align-items-center gap-2 flex-wrap">
              <div className="dateRange-custom-width position-relative">
                <DatePicker
                  selected={dateRangeFrom}
                  onChange={(date) => {
                    if (date) setDateRangeFrom(date);
                    setIsFromOpen(false);
                  }}
                  open={isFromOpen}
                  onInputClick={() => setIsFromOpen(true)}
                  onClickOutside={() => setIsFromOpen(false)}
                  placeholderText={t("from")}
                  className="form-control form-control-sm w-100 pe-4"
                  dateFormat="dd-MM-yyyy"
                  maxDate={dateRangeTo || undefined}
                  popperPlacement="bottom-start"
                />
                <span
                  className="position-absolute top-50 end-0 translate-middle-y pe-2 fs-10 lh-1 cursor-pointer z-index-1"
                  onClick={() => setIsFromOpen((prev) => !prev)}
                >
                  <FontAwesomeIcon icon={faCalendarAlt} className="text-muted" />
                </span>
              </div>
              <span className="text-muted small">{t("to")}</span>
              <div className="dateRange-custom-width position-relative">
                <DatePicker
                  selected={dateRangeTo}
                  onChange={(date) => {
                    if (date) setDateRangeTo(date);
                    setIsToOpen(false);
                  }}
                  open={isToOpen}
                  onInputClick={() => setIsToOpen(true)}
                  onClickOutside={() => setIsToOpen(false)}
                  minDate={dateRangeFrom || undefined}
                  placeholderText={t("to")}
                  className="form-control form-control-sm w-100 pe-4"
                  dateFormat="dd-MM-yyyy"
                  popperPlacement="bottom-start"
                />
                <span
                  className="position-absolute top-50 end-0 translate-middle-y pe-2 fs-10 cursor-pointer z-index-1"
                  onClick={() => setIsToOpen((prev) => !prev)}
                >
                  <FontAwesomeIcon icon={faCalendarAlt} className="text-muted" />
                </span>
              </div>

              <Form.Select
                value={filterOption}
                onChange={(e) => {
                  const value = e.target.value;
                  setFilterOption(value);
                  localStorage.setItem("ordersFilterOption", value);
                  const { startDate, endDate } = getDateRangeForOption(value);
                  setDateRangeFrom(startDate);
                  setDateRangeTo(endDate);
                }}
                size="sm"
                style={{ maxWidth: "180px" }}
              >
                <option value="all">{t("allLabel")}</option>
                <option value="currentWeek">{t("currentWeek")}</option>
                <option value="nextWeek">{t("nextWeek")}</option>
                <option value="currentMonth">{t("currentMonth")}</option>
              </Form.Select>

              <div style={{ minWidth: "200px" }}>
                <AdvanceTableSearchBox setGlobalFilter={setSearchInput} />
              </div>
            </Col>
          </Row>
        </Card.Header>

        <Card.Body className="p-0">
          {orders.length === 0 ? (
            <div className="text-center py-5">{t("noDataFoundForOrders")}</div>
          ) : (
            <AdvanceTable
              headerClassName="bg-200 text-nowrap align-middle"
              rowClassName="align-middle white-space-nowrap"
              tableProps={{
                bordered: true,
                striped: true,
                size: "sm",
                className: "mb-0 overflow-hidden data-font-size table-hover",
              }}
              getRowProps={(row) => ({
                onClick: () => handleViewDetails(row.original),
                style: { cursor: "pointer" },
              })}
            />
          )}
        </Card.Body>
        <Card.Footer>
          <ManualPaginationFooter
            totalCount={totalCount}
            pageIndex={pageIndex}
            pageSize={pageSize}
            tableKey="orderTable"
            onPageSizeChange={(newSize) => {
              setPageSize(newSize);
              setPageIndex(0);
            }}
          />
        </Card.Footer>
      </Card>
    </AdvanceTableProvider>
  );
};

export default Orders;